import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material'
import { Close as CloseIcon, VpnKey as PasswordIcon } from '@mui/icons-material'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useDispatch } from 'react-redux'
import { setMessage } from '../../store/settings/settings.actions'
import { EventType, Severity } from '@3m5/crude-frontend/dist/shared/types/messageTypes'
import { passwordApi } from '../api/passwordApi'

interface AdminPasswordChangeDialogProps {
  open: boolean
  onClose: () => void
  userId: string
  userName: string
}

export const AdminPasswordChangeDialog: React.FC<AdminPasswordChangeDialogProps> = ({
  open,
  onClose,
  userId,
  userName,
}) => {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const dispatch = useDispatch()

  const validatePassword = (password: string): string[] => {
    const errors: string[] = []

    if (password.length < 8) {
      errors.push('Passwort muss mindestens 8 Zeichen lang sein')
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Passwort muss mindestens einen Großbuchstaben enthalten')
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Passwort muss mindestens einen Kleinbuchstaben enthalten')
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Passwort muss mindestens eine Ziffer enthalten')
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.push('Passwort muss mindestens ein Sonderzeichen enthalten')
    }

    return errors
  }

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {}

    // Validierung
    const passwordErrors = validatePassword(newPassword)
    if (passwordErrors.length > 0) {
      newErrors.password = passwordErrors[0]
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwörter stimmen nicht überein'
    }

    if (!reason.trim()) {
      newErrors.reason = 'Bitte geben Sie einen Grund an'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    try {
      const response = await passwordApi.changeUserPasswordAsAdmin(userId, {
        newPassword,
        reason,
      })

      if (response.ok) {
        dispatch(setMessage({
          severity: Severity.success,
          message: `Passwort für ${userName} erfolgreich geändert`,
          eventType: EventType.update,
        }))
        handleClose()
      } else {
        const errorData = await response.json()
        dispatch(setMessage({
          severity: Severity.error,
          message: errorData.message || 'Fehler beim Ändern des Passworts',
          eventType: EventType.update,
        }))
      }
    } catch (error) {
      dispatch(setMessage({
        severity: Severity.error,
        message: 'Fehler beim Ändern des Passworts',
        eventType: EventType.update,
      }))
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setNewPassword('')
    setConfirmPassword('')
    setReason('')
    setErrors({})
    setShowPassword(false)
    setShowPasswordRepeat(false)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box display='flex' alignItems='center' gap={1}>
            <PasswordIcon />
            <Typography variant='h6'>Admin: Passwort ändern</Typography>
          </Box>
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity='info' sx={{ mb: 3 }}>
            Sie ändern das Passwort für den Nutzer <strong>{userName}</strong>.
            Der Nutzer wird per E-Mail benachrichtigt.
          </Alert>

          <TextField
            fullWidth
            label='Neues Passwort'
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value)
              setErrors(prev => ({ ...prev, password: '' }))
            }}
            error={!!errors.password}
            helperText={errors.password}
            margin='normal'
            required
            InputProps={{
              endAdornment: (
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={() => setShowPassword(!showPassword)}
                  edge='end'
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
          />

          <TextField
            fullWidth
            label='Passwort bestätigen'
            type={showPasswordRepeat ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value)
              setErrors(prev => ({ ...prev, confirmPassword: '' }))
            }}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            margin='normal'
            required
            InputProps={{
              endAdornment: (
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}
                  edge='end'
                >
                  {showPasswordRepeat ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
          />

          <TextField
            fullWidth
            label='Grund für die Änderung'
            multiline
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value)
              setErrors(prev => ({ ...prev, reason: '' }))
            }}
            error={!!errors.reason}
            helperText={errors.reason || 'Diese Information wird im Audit-Log gespeichert'}
            margin='normal'
            required
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant='caption' color='text.secondary'>
              Passwort-Anforderungen:
            </Typography>
            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
              <li><Typography variant='caption'>Mindestens 8 Zeichen</Typography></li>
              <li><Typography variant='caption'>Mindestens ein Großbuchstabe</Typography></li>
              <li><Typography variant='caption'>Mindestens ein Kleinbuchstabe</Typography></li>
              <li><Typography variant='caption'>Mindestens eine Ziffer</Typography></li>
              <li><Typography variant='caption'>Mindestens ein Sonderzeichen</Typography></li>
            </ul>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={loading || !newPassword || newPassword !== confirmPassword}
          startIcon={loading ? <CircularProgress size={20} /> : <PasswordIcon />}
        >
          Passwort ändern
        </Button>
      </DialogActions>
    </Dialog>
  )
}
